import {
  Check,
  Copy,
  Download,
  FileText,
  Globe2,
  Home,
  Info,
  LogIn,
  LogOut,
  PenLine,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Upload,
} from "lucide-react";
import { PointerEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  deleteStoredFile,
  downloadBlob,
  fetchStoredFileBlob,
  getSession,
  listFiles,
  signOut,
  startGoogleSignIn,
  StoredFile,
  uploadStoredFile,
  User,
} from "./lib/api";
import { exportEditorHtmlToDocx, readDocxAsHtml } from "./lib/docx";
import { loadPdfDocument, PdfDocument, PdfRenderResult, renderPdfDocumentPage, signPdf } from "./lib/pdf";
import { detectLocale, dictionaries, Locale } from "./i18n";

type Point = { x: number; y: number };
type Stroke = Point[];
type SignatureAsset = {
  name: string;
  dataUrl: string;
  blob: Blob;
  storedId?: string;
};

type Placement = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type AppRoute = "/" | "/sign";

const signatureMime = "image/png";

export default function App() {
  const [locale, setLocale] = useState<Locale>(() => {
    const saved = window.localStorage.getItem("tools-locale") as Locale | null;
    return saved === "en" || saved === "zh-TW" ? saved : detectLocale();
  });
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [activeSignature, setActiveSignature] = useState<SignatureAsset | null>(null);
  const [pdfImport, setPdfImport] = useState<{ file: File; token: number } | null>(null);
  const [wordImport, setWordImport] = useState<{ file: File; token: number } | null>(null);
  const [hasGuestWork, setHasGuestWork] = useState(false);
  const [toast, setToast] = useState("");
  const [route, setRoute] = useState<AppRoute>(() => normalizeRoute(window.location.pathname));

  const t = useCallback((key: string) => dictionaries[locale][key] ?? key, [locale]);

  const navigate = useCallback((nextRoute: AppRoute) => {
    if (window.location.pathname !== nextRoute) {
      window.history.pushState({}, "", nextRoute);
    }
    setRoute(nextRoute);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem("tools-locale", locale);
  }, [locale]);

  useEffect(() => {
    getSession()
      .then((sessionUser) => setUser(sessionUser))
      .finally(() => setAuthReady(true));
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(normalizeRoute(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const refreshFiles = useCallback(async () => {
    if (!user) {
      setFiles([]);
      return;
    }
    setFiles(await listFiles());
  }, [user]);

  useEffect(() => {
    refreshFiles();
  }, [refreshFiles]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("auth_error")) {
      setToast(t("authError"));
      params.delete("auth_error");
      const nextSearch = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`);
    }
  }, [t]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!user && hasGuestWork) {
        event.preventDefault();
        event.returnValue = t("refreshConfirm");
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasGuestWork, t, user]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleLocaleChange = (nextLocale: Locale) => setLocale(nextLocale);

  const handleSignatureReady = (signature: SignatureAsset) => {
    setActiveSignature(signature);
    if (!user) setHasGuestWork(true);
  };

  const handleSaveSignature = async () => {
    if (!activeSignature || !user) return;
    const file = await uploadStoredFile({
      file: activeSignature.blob,
      kind: "signature",
      name: activeSignature.name || t("signatureName"),
      metadata: { transparent: true },
    });
    setActiveSignature({ ...activeSignature, storedId: file.id, name: file.name });
    setToast(t("savedOk"));
    await refreshFiles();
  };

  const handleCopySignature = async () => {
    if (!activeSignature) return;
    await navigator.clipboard.write([
      new ClipboardItem({
        [signatureMime]: activeSignature.blob,
      }),
    ]);
    setToast(t("copied"));
  };

  const useStoredFile = async (file: StoredFile) => {
    const blob = await fetchStoredFileBlob(file);
    if (file.kind === "signature") {
      const dataUrl = await blobToDataUrl(blob);
      setActiveSignature({ name: file.name, dataUrl, blob, storedId: file.id });
      setToast(t("ready"));
      return;
    }
    if (file.kind === "pdf") {
      setPdfImport({ file: new File([blob], file.name, { type: file.mimeType }), token: Date.now() });
      setToast(t("ready"));
      return;
    }
    if (file.kind === "word") {
      setWordImport({ file: new File([blob], file.name, { type: file.mimeType }), token: Date.now() });
      setToast(t("ready"));
    }
  };

  const handleDelete = async (file: StoredFile) => {
    await deleteStoredFile(file.id);
    await refreshFiles();
  };

  return (
    <div className="app">
      <header className="navbar">
        <a
          className="brand"
          href="/"
          aria-label="Tools"
          onClick={(event) => {
            event.preventDefault();
            navigate("/");
          }}
        >
          <span className="brandMark">T</span>
          <span>{t("appName")}</span>
        </a>
        <div className="functionList" aria-label={t("toolsList")}>
          <button
            className={route === "/" ? "toolNavButton active" : "toolNavButton"}
            type="button"
            onClick={() => navigate("/")}
            title={t("home")}
            aria-label={t("home")}
          >
            <Home size={16} aria-hidden="true" />
            <span className="toolNavText">{t("home")}</span>
          </button>
          <button
            className={route === "/sign" ? "toolNavButton active" : "toolNavButton"}
            type="button"
            onClick={() => navigate("/sign")}
            title={t("onlineSignature")}
            aria-label={t("onlineSignature")}
          >
            <PenLine size={16} aria-hidden="true" />
            <span className="toolNavText">{t("onlineSignature")}</span>
          </button>
        </div>
        <nav className="navActions" aria-label="Primary">
          <label className="languagePicker" title={t("language")}>
            <Globe2 size={16} aria-hidden="true" />
            <select
              value={locale}
              aria-label={t("language")}
              onChange={(event) => handleLocaleChange(event.target.value as Locale)}
            >
              <option value="zh-TW">繁</option>
              <option value="en">EN</option>
            </select>
          </label>
          <span
            className={user ? "statusBadge saved" : "statusBadge"}
            title={user ? `${t("saved")}: ${user.email}` : t("refreshWarning")}
            aria-label={user ? `${t("saved")}: ${user.email}` : t("guest")}
          >
            {user?.avatarUrl ? <img className="navAvatar" src={user.avatarUrl} alt="" /> : <span className="statusDot" />}
            <span className="srOnly">{user ? t("saved") : t("guest")}</span>
          </span>
          {user ? (
            <button
              className="navIconButton"
              type="button"
              title={t("signOut")}
              aria-label={t("signOut")}
              onClick={async () => {
                await signOut();
                setUser(null);
                setFiles([]);
              }}
            >
              <LogOut size={16} aria-hidden="true" />
            </button>
          ) : (
            <button
              className="navIconButton primary"
              type="button"
              onClick={() => startGoogleSignIn(route)}
              disabled={!authReady}
              title={t("signIn")}
              aria-label={t("signIn")}
            >
              <LogIn size={16} aria-hidden="true" />
            </button>
          )}
        </nav>
      </header>

      {route === "/" ? (
        <HomePage t={t} onOpenSign={() => navigate("/sign")} />
      ) : (
      <main className="workspace" id="sign">
        <section className="toolPanel signaturePanel" aria-labelledby="signature-title">
          <div className="sectionHeader">
            <div>
              <p className="eyebrow">{t("draw")}</p>
              <h1 id="signature-title">{t("signature")}</h1>
            </div>
            <PenLine size={22} aria-hidden="true" />
          </div>
          <SignaturePad onReady={handleSignatureReady} t={t} />
          <div className="toolbar compactToolbar" aria-label={t("signature")}>
            <button
              className="iconButton actionIconButton"
              type="button"
              onClick={() => activeSignature && downloadBlob(activeSignature.blob, activeSignature.name || t("signatureName"))}
              disabled={!activeSignature}
              title={t("download")}
              aria-label={t("download")}
            >
              <Download size={16} aria-hidden="true" />
            </button>
            <button
              className="iconButton actionIconButton"
              type="button"
              onClick={handleCopySignature}
              disabled={!activeSignature || !("ClipboardItem" in window)}
              title={t("copy")}
              aria-label={t("copy")}
            >
              <Copy size={16} aria-hidden="true" />
            </button>
            <button
              className="iconButton actionIconButton"
              type="button"
              onClick={handleSaveSignature}
              disabled={!activeSignature || !user}
              title={user ? t("save") : t("loginToSave")}
              aria-label={user ? t("save") : t("loginToSave")}
            >
              <Save size={16} aria-hidden="true" />
            </button>
          </div>
          {activeSignature ? (
            <div className="signaturePreview">
              <img src={activeSignature.dataUrl} alt={t("signature")} />
            </div>
          ) : null}
        </section>

        <section className="toolPanel documentPanel" aria-labelledby="documents-title">
          <div className="sectionHeader">
            <div>
              <p className="eyebrow">{t("editor")}</p>
              <h2 id="documents-title">{t("documents")}</h2>
            </div>
            <FileText size={22} aria-hidden="true" />
          </div>
          <div className="documentGrid">
            <PdfSigner
              activeSignature={activeSignature}
              importFile={pdfImport}
              user={user}
              t={t}
              onGuestWork={() => setHasGuestWork(true)}
              onSaved={refreshFiles}
              notify={setToast}
            />
            <WordSigner
              activeSignature={activeSignature}
              importFile={wordImport}
              user={user}
              t={t}
              onGuestWork={() => setHasGuestWork(true)}
              onSaved={refreshFiles}
              notify={setToast}
            />
          </div>
        </section>

        <section className="savedPanel" aria-labelledby="saved-title">
          <div className="sectionHeader compactHeader">
            <div>
              <p className="eyebrow">{t("profile")}</p>
              <h2 id="saved-title">{t("savedFiles")}</h2>
            </div>
            {user?.avatarUrl ? <img className="avatar" src={user.avatarUrl} alt={user.name} /> : <Check size={20} />}
          </div>
          {!user ? <p className="muted">{t("uploadHint")}</p> : null}
          {user && files.length === 0 ? <p className="muted">{t("noSavedFiles")}</p> : null}
          <div className="fileList">
            {files.map((file) => (
              <article className="fileItem" key={file.id}>
                <div>
                  <strong>{file.name}</strong>
                  <span>{file.kind.toUpperCase()} · {formatBytes(file.size)}</span>
                </div>
                <div className="fileActions">
                  {file.kind === "signature" || file.kind === "pdf" || file.kind === "word" ? (
                    <button className="iconButton" type="button" onClick={() => useStoredFile(file)} title={t("reuse")}>
                      <PenLine size={16} aria-hidden="true" />
                    </button>
                  ) : null}
                  <button className="iconButton" type="button" onClick={() => downloadStored(file)} title={t("download")}>
                    <Download size={16} aria-hidden="true" />
                  </button>
                  <button className="iconButton danger" type="button" onClick={() => handleDelete(file)} title={t("delete")}>
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      )}

      {toast ? <div className="toast">{toast}</div> : null}
    </div>
  );
}

function HomePage({ t, onOpenSign }: { t: (key: string) => string; onOpenSign: () => void }) {
  return (
    <main className="homePage">
      <section className="homeIntro" aria-labelledby="home-title">
        <div>
          <p className="eyebrow">{t("toolsList")}</p>
          <h1 id="home-title">{t("homeTitle")}</h1>
          <p>{t("homeSubtitle")}</p>
        </div>
        <div className="homeHighlights" aria-label={t("homeHighlights")}>
          <span title={t("guestMode")}>
            <FileText size={16} aria-hidden="true" />
            {t("guestMode")}
          </span>
          <span title={t("savedMode")}>
            <Save size={16} aria-hidden="true" />
            {t("savedMode")}
          </span>
        </div>
      </section>

      <section className="homeToolGrid" aria-label={t("toolsList")}>
        <button className="toolCard primaryToolCard" type="button" onClick={onOpenSign}>
          <span className="toolCardIcon">
            <PenLine size={22} aria-hidden="true" />
          </span>
          <span className="toolCardBody">
            <strong>{t("onlineSignature")}</strong>
            <span>{t("onlineSignatureDesc")}</span>
          </span>
        </button>
        <article className="toolCard upcomingTool" title={t("moreToolsDesc")}>
          <span className="toolCardIcon mutedIcon">
            <Plus size={22} aria-hidden="true" />
          </span>
          <span className="toolCardBody">
            <strong>{t("moreTools")}</strong>
            <span>{t("moreToolsDesc")}</span>
          </span>
        </article>
      </section>
    </main>
  );
}

function SignaturePad({ onReady, t }: { onReady: (signature: SignatureAsset) => void; t: (key: string) => string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const strokesRef = useRef<Stroke[]>([]);
  const drawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const preventGestureDefault = (event: Event) => event.preventDefault();
    canvas.addEventListener("touchstart", preventGestureDefault, { passive: false });
    canvas.addEventListener("touchmove", preventGestureDefault, { passive: false });
    canvas.addEventListener("selectstart", preventGestureDefault);
    return () => {
      canvas.removeEventListener("touchstart", preventGestureDefault);
      canvas.removeEventListener("touchmove", preventGestureDefault);
      canvas.removeEventListener("selectstart", preventGestureDefault);
    };
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, rect.width, rect.height);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = "#111827";
    context.lineWidth = 2.8;

    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      context.beginPath();
      context.moveTo(stroke[0].x, stroke[0].y);
      for (const point of stroke.slice(1)) context.lineTo(point.x, point.y);
      context.stroke();
    }
  }, []);

  useEffect(() => {
    strokesRef.current = strokes;
    redraw();
  }, [redraw, strokes]);

  useEffect(() => {
    const onResize = () => redraw();
    window.addEventListener("resize", onResize);
    redraw();
    return () => window.removeEventListener("resize", onResize);
  }, [redraw]);

  const pointerPoint = (event: PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    const next = [...strokesRef.current, [pointerPoint(event)]];
    strokesRef.current = next;
    setStrokes(next);
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    const next = [...strokesRef.current];
    next[next.length - 1] = [...next[next.length - 1], pointerPoint(event)];
    strokesRef.current = next;
    setStrokes(next);
  };

  const end = async (event?: PointerEvent<HTMLCanvasElement>) => {
    event?.preventDefault();
    drawingRef.current = false;
    window.getSelection()?.removeAllRanges();
    await emitSignature();
  };

  const emitSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas || strokesRef.current.length === 0) return;
    const blob = await canvasToBlob(canvas);
    const dataUrl = canvas.toDataURL(signatureMime);
    onReady({ name: t("signatureName"), dataUrl, blob });
  };

  return (
    <div className="signaturePadWrap">
      <canvas
        ref={canvasRef}
        className="signaturePad"
        aria-label={t("signature")}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <div className="miniToolbar">
        <button className="iconButton" type="button" onClick={() => setStrokes((value) => value.slice(0, -1))} title={t("undo")}>
          <RotateCcw size={16} aria-hidden="true" />
        </button>
        <button
          className="iconButton"
          type="button"
          onClick={() => {
            strokesRef.current = [];
            setStrokes([]);
            redraw();
          }}
          title={t("clear")}
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function PdfSigner({
  activeSignature,
  importFile,
  user,
  t,
  onGuestWork,
  onSaved,
  notify,
}: {
  activeSignature: SignatureAsset | null;
  importFile: { file: File; token: number } | null;
  user: User | null;
  t: (key: string) => string;
  onGuestWork: () => void;
  onSaved: () => void;
  notify: (message: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<Point | null>(null);
  const pdfDocumentRef = useRef<PdfDocument | null>(null);
  const renderAbortRef = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PdfDocument | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [pageCount, setPageCount] = useState(1);
  const [renderInfo, setRenderInfo] = useState<PdfRenderResult | null>(null);
  const [placement, setPlacement] = useState<Placement>({ x: 48, y: 48, width: 190, height: 76 });
  const [pdfError, setPdfError] = useState("");

  const render = useCallback(async () => {
    if (!pdfDocument || !canvasRef.current || stageWidth <= 0) return;

    renderAbortRef.current?.abort();
    const abortController = new AbortController();
    renderAbortRef.current = abortController;
    const maxWidth = Math.max(280, stageWidth - 24);

    try {
      const info = await renderPdfDocumentPage(
        pdfDocument,
        canvasRef.current,
        clampPage(pageNumber, pdfDocument.numPages),
        maxWidth,
        abortController.signal,
      );
      if (abortController.signal.aborted) return;
      setPdfError("");
      setRenderInfo(info);
      setPageCount(info.pageCount);
      setPlacement((current) => constrainPlacement(current, info.viewportWidth, info.viewportHeight));
    } catch (error) {
      if (abortController.signal.aborted || String(error).includes("PDF render aborted")) return;
      console.error(error);
      setPdfError(t("pdfRenderError"));
    }
  }, [pageNumber, pdfDocument, stageWidth, t]);

  useEffect(() => {
    render();
  }, [render]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const updateWidth = (width: number) => {
      const roundedWidth = Math.round(width);
      setStageWidth((current) => (Math.abs(current - roundedWidth) > 8 ? roundedWidth : current));
    };

    updateWidth(stage.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) updateWidth(entry.contentRect.width);
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      renderAbortRef.current?.abort();
      pdfDocumentRef.current?.destroy();
    };
  }, []);

  const onFile = async (selected: File | null) => {
    if (!selected) return;
    const buffer = await selected.arrayBuffer();
    const document = await loadPdfDocument(buffer.slice(0));
    renderAbortRef.current?.abort();
    pdfDocumentRef.current?.destroy();
    pdfDocumentRef.current = document;
    setFile(selected);
    setBytes(buffer);
    setPdfDocument(document);
    setPageNumber(1);
    setPageInput("1");
    setPageCount(document.numPages);
    setRenderInfo(null);
    setPdfError("");
    onGuestWork();
  };

  useEffect(() => {
    if (importFile) {
      onFile(importFile.file);
    }
  }, [importFile?.token]);

  const exportPdf = async () => {
    if (!bytes || !renderInfo || !activeSignature) {
      notify(t("noSignature"));
      return;
    }
    const signatureBytes = await activeSignature.blob.arrayBuffer();
    const blob = await signPdf(bytes.slice(0), signatureBytes, {
      pageNumber,
      ...placement,
      viewportWidth: renderInfo.viewportWidth,
      viewportHeight: renderInfo.viewportHeight,
    });
    downloadBlob(blob, t("pdfName"));
    notify(t("exportedOk"));
    if (user) {
      await uploadStoredFile({ file: blob, kind: "export", name: t("pdfName"), metadata: { source: file?.name ?? "pdf" } });
      await onSaved();
    }
  };

  const handlePageChange = (value: string) => {
    if (!pdfDocument) return;
    if (value.trim() === "") {
      setPageInput("");
      return;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;

    const nextPage = clampPage(parsed, pageCount);
    setPageNumber(nextPage);
    setPageInput(String(nextPage));
  };

  const handlePageBlur = () => {
    if (!pdfDocument) {
      setPageInput("1");
      return;
    }
    const parsed = Number(pageInput);
    const nextPage = clampPage(Number.isFinite(parsed) ? parsed : pageNumber, pageCount);
    setPageNumber(nextPage);
    setPageInput(String(nextPage));
  };

  const saveOriginal = async () => {
    if (!file || !user) return;
    await uploadStoredFile({ file, kind: "pdf", name: file.name, metadata: { original: true } });
    notify(t("savedOk"));
    await onSaved();
  };

  return (
    <article className="editorSurface">
      <div className="editorTopline">
        <h3>{t("pdf")}</h3>
        <span className="hintIcon" title={t("fileLimit")} aria-label={t("fileLimit")}>
          <Info size={15} aria-hidden="true" />
        </span>
      </div>
      <label className="dropButton">
        <Upload size={16} aria-hidden="true" />
        {file?.name || t("choosePdf")}
        <input type="file" accept="application/pdf,.pdf" onChange={(event) => onFile(event.target.files?.[0] ?? null)} />
      </label>
      <div className="pdfStage" ref={stageRef}>
        <canvas
          ref={canvasRef}
          aria-label={t("pdf")}
          onPointerDown={(event) => {
            event.preventDefault();
            window.getSelection()?.removeAllRanges();
          }}
        />
        {activeSignature && renderInfo ? (
          <img
            className="pdfSignature"
            src={activeSignature.dataUrl}
            alt={t("signature")}
            style={{
              transform: `translate(${placement.x}px, ${placement.y}px)`,
              width: `${placement.width}px`,
              height: `${placement.height}px`,
            }}
            onPointerDown={(event) => {
              event.preventDefault();
              window.getSelection()?.removeAllRanges();
              event.currentTarget.setPointerCapture(event.pointerId);
              dragRef.current = { x: event.clientX - placement.x, y: event.clientY - placement.y };
            }}
            onPointerMove={(event) => {
              if (!dragRef.current || !renderInfo) return;
              event.preventDefault();
              setPlacement((current) =>
                constrainPlacement(
                  { ...current, x: event.clientX - dragRef.current!.x, y: event.clientY - dragRef.current!.y },
                  renderInfo.viewportWidth,
                  renderInfo.viewportHeight,
                ),
              );
            }}
            onPointerUp={(event) => {
              event.preventDefault();
              dragRef.current = null;
            }}
            onPointerCancel={() => {
              dragRef.current = null;
            }}
            title={t("dragHint")}
          />
        ) : null}
        {pdfError ? <div className="pdfError">{pdfError}</div> : null}
      </div>
      <div className="editorControls">
        <label>
          {t("page")} {pdfDocument ? `/ ${pageCount}` : ""}
          <input
            type="number"
            min={1}
            max={pageCount}
            value={pageInput}
            inputMode="numeric"
            onChange={(event) => handlePageChange(event.target.value)}
            onBlur={handlePageBlur}
            disabled={!pdfDocument}
          />
        </label>
        <label>
          {t("size")}
          <input
            type="range"
            min={90}
            max={320}
            value={placement.width}
            onChange={(event) => {
              const width = Number(event.target.value);
              setPlacement((current) => ({ ...current, width, height: Math.round(width * 0.4) }));
            }}
            disabled={!renderInfo}
          />
        </label>
      </div>
      <div className="toolbar compactToolbar">
        <button
          className="iconButton actionIconButton"
          type="button"
          onClick={exportPdf}
          disabled={!bytes || !activeSignature}
          title={t("export")}
          aria-label={t("export")}
        >
          <Download size={16} aria-hidden="true" />
        </button>
        <button
          className="iconButton actionIconButton"
          type="button"
          onClick={saveOriginal}
          disabled={!file || !user}
          title={user ? t("save") : t("loginToSave")}
          aria-label={user ? t("save") : t("loginToSave")}
        >
          <Save size={16} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

function WordSigner({
  activeSignature,
  importFile,
  user,
  t,
  onGuestWork,
  onSaved,
  notify,
}: {
  activeSignature: SignatureAsset | null;
  importFile: { file: File; token: number } | null;
  user: User | null;
  t: (key: string) => string;
  onGuestWork: () => void;
  onSaved: () => void;
  notify: (message: string) => void;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [html, setHtml] = useState("<p></p>");

  const onFile = async (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    const nextHtml = await readDocxAsHtml(selected);
    setHtml(nextHtml);
    window.setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = nextHtml;
    }, 0);
    onGuestWork();
  };

  useEffect(() => {
    if (importFile) {
      onFile(importFile.file);
    }
  }, [importFile?.token]);

  const insertSignature = () => {
    if (!activeSignature || !editorRef.current) {
      notify(t("noSignature"));
      return;
    }
    editorRef.current.focus();
    document.execCommand(
      "insertHTML",
      false,
      `<img data-signature="true" class="inlineSignature" src="${activeSignature.dataUrl}" alt="${t("signature")}" />`,
    );
  };

  const exportWord = async () => {
    if (!editorRef.current) return;
    const blob = await exportEditorHtmlToDocx(editorRef.current.innerHTML);
    downloadBlob(blob, t("wordName"));
    notify(t("exportedOk"));
    if (user) {
      await uploadStoredFile({ file: blob, kind: "export", name: t("wordName"), metadata: { source: file?.name ?? "word" } });
      await onSaved();
    }
  };

  const saveOriginal = async () => {
    if (!file || !user) return;
    await uploadStoredFile({ file, kind: "word", name: file.name, metadata: { original: true } });
    notify(t("savedOk"));
    await onSaved();
  };

  return (
    <article className="editorSurface">
      <div className="editorTopline">
        <h3>{t("word")}</h3>
        <span className="hintIcon" title={t("wordFormatHint")} aria-label={t("wordFormatHint")}>
          <Info size={15} aria-hidden="true" />
        </span>
      </div>
      <label className="dropButton">
        <Upload size={16} aria-hidden="true" />
        {file?.name || t("chooseWord")}
        <input
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(event) => onFile(event.target.files?.[0] ?? null)}
        />
      </label>
      <div
        className="wordEditor"
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => {
          if (editorRef.current) setHtml(editorRef.current.innerHTML);
          onGuestWork();
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <div className="toolbar compactToolbar">
        <button
          className="iconButton actionIconButton"
          type="button"
          onClick={insertSignature}
          disabled={!activeSignature}
          title={t("insertSignature")}
          aria-label={t("insertSignature")}
        >
          <PenLine size={16} aria-hidden="true" />
        </button>
        <button className="iconButton actionIconButton" type="button" onClick={exportWord} title={t("export")} aria-label={t("export")}>
          <Download size={16} aria-hidden="true" />
        </button>
        <button
          className="iconButton actionIconButton"
          type="button"
          onClick={saveOriginal}
          disabled={!file || !user}
          title={user ? t("save") : t("loginToSave")}
          aria-label={user ? t("save") : t("loginToSave")}
        >
          <Save size={16} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas export failed"));
    }, signatureMime);
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function constrainPlacement(placement: Placement, width: number, height: number): Placement {
  const nextWidth = Math.min(placement.width, Math.max(90, width));
  const nextHeight = Math.min(placement.height, Math.max(40, height));
  return {
    ...placement,
    width: nextWidth,
    height: nextHeight,
    x: Math.max(0, Math.min(placement.x, width - nextWidth)),
    y: Math.max(0, Math.min(placement.y, height - nextHeight)),
  };
}

function clampPage(pageNumber: number, pageCount: number): number {
  if (!Number.isFinite(pageNumber)) return 1;
  return Math.max(1, Math.min(Math.trunc(pageNumber), Math.max(1, pageCount)));
}

function normalizeRoute(pathname: string): AppRoute {
  return pathname === "/sign" ? "/sign" : "/";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function downloadStored(file: StoredFile) {
  const blob = await fetchStoredFileBlob(file);
  downloadBlob(blob, file.name);
}
